// Shared parts of the operating-cadence screens (MEETINGS): the pages' gate and the hover icon
// action every row uses (visible controls: add, archive, delete, reorder are icons on hover,
// never only in a menu).
import { Button, cn, Tooltip, TooltipContent, TooltipTrigger } from "@companyos/ui";
import type { ReactNode } from "react";
import { modeAccess, rulesByKey } from "../../extensions/modes";
import { NoAccessPage } from "../../pages/status-pages";
import { useCompany, useSession } from "../use-feature";

/**
 * The cadence pages' gate, as Priorities': open to whoever may open Cadence or the chart (the
 * routes are `core`; the server cuts every list to what the caller may see). Full height: each
 * page scrolls under its own header.
 */
export function CadenceGate({ what, children }: { what: string; children: ReactNode }) {
  const { me } = useSession();
  const company = useCompany();
  const ok = ["cadence", "chart"].some((key) => {
    const mode = rulesByKey(key);
    return mode ? modeAccess(mode, me, company) === "ok" : false;
  });
  if (!ok) return <NoAccessPage what={what} slug={company.slug} />;
  return (
    <div data-mode-root="cadence" className="flex h-full min-h-0 flex-col">
      {children}
    </div>
  );
}

export function IconAction({
  label,
  tip,
  danger = false,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  tip: string;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={label}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className={cn(
              "text-faint",
              danger ? "hover:text-danger-text" : "hover:text-foreground",
            )}
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}

/** The row's hover strip: hidden until the row is hovered or focused (always on touch). */
export const HOVER_STRIP =
  "flex items-center gap-0.5 opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100 pointer-coarse:opacity-100";
